/**
 * Dynamic Property Field
 *
 * Renders the appropriate field component based on property type
 */

import type { InferredPropertySchema } from '@/types/inferred-schema';
import { useState, useEffect, useRef } from 'react';
import { useIdentity } from '@/hooks/useIdentity';
import {
  StringField,
  NumberField,
  BooleanField,
  SelectField,
  ArrayField,
  ObjectField,
  CredentialField,
  CollectionField,
  FieldMappingField,
  NoticeField,
  MCPToolSelectorField,
  WorkflowSelectorField,
  ButtonField,
  ChatVisibilityField,
  ModeToggleField,
  FileUploadField,
} from './fields';
import { CodeMirrorEditor, EditorLanguage } from '../node-editor/CodeMirrorEditor';
import { Label } from '@/components/ui/label';
import { HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FixedCollectionPropertyRenderer } from '@/components/properties/renderers/FixedCollectionPropertyRenderer';
import { JsonSchemaEditorRenderer } from '@/components/properties/renderers/JsonSchemaEditorRenderer';
import { MCPTestMessageField } from './MCPTestMessageField';
import { ChatTriggerTestInputField } from './ChatTriggerTestInputField';
import { EmbedCodeField } from './EmbedCodeField';
import { PropertyErrorBoundary } from '@/components/error/PropertyErrorBoundary';
import { useWorkflowStore } from '@/stores/workflow-store';

interface InputData {
  nodeId: string;
  nodeName: string;
  output: any;
  sourceHandle?: string;
  nodeDistance: number;
}

interface DynamicPropertyFieldProps {
  property: InferredPropertySchema;
  value: any;
  onChange: (value: any) => void;
  onParameterChange?: (key: string, value: any) => void;
  onBatchParameterChange?: (updates: Record<string, any>) => void; // Batch update multiple parameters at once
  onPropertyChange?: (key: string, value: any) => void; // Callback to update other properties
  error?: string;
  nodeId?: string;
  nodes?: any[];
  allParameters?: Record<string, any>;
  nodeType?: string;
  credentialId?: string;
  inputData?: InputData[];
  onOpenAIHelp?: () => void; // Callback to open AI Help tab (for Code nodes)
}

export function DynamicPropertyField({
  property,
  value,
  onChange,
  onParameterChange,
  onBatchParameterChange,
  onPropertyChange,
  error,
  nodeId,
  nodes,
  allParameters,
  nodeType,
  credentialId,
  inputData = [],
  onOpenAIHelp,
}: DynamicPropertyFieldProps) {
  // Get workflow from store (same as NoticePropertyRenderer)
  const currentWorkflow = useWorkflowStore((state) => state.currentWorkflow);
  const currentWorkflowId = currentWorkflow?.id;

  // Get user identity for auto-populating email fields
  const { user } = useIdentity();
  const hasAutoPopulated = useRef(false);

  // Auto-populate user email for access-mail 'from' field
  useEffect(() => {
    if (
      nodeType === 'access-mail' &&
      property.key === 'from' &&
      !value &&
      user?.profile?.email &&
      !hasAutoPopulated.current
    ) {
      hasAutoPopulated.current = true;
      onChange(user.profile.email as string);
    }
  }, [nodeType, property.key, value, user, onChange]);

  // GraphQL schema for code editor (lazy loaded)
  const [graphqlSchema, setGraphqlSchema] = useState<string | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);

  // Lazy load GraphQL schema when needed
  useEffect(() => {
    const schemaPath = (property as any).typeOptions?.graphqlSchemaPath;
    if (property.type === 'code' && schemaPath && !graphqlSchema && !schemaLoading) {
      setSchemaLoading(true);
      // Dynamic import of the schema file
      import(`@/shared/nodes/access-integrations/access-financials/schema.graphql?raw`)
        .then((module) => {
          setGraphqlSchema(module.default);
          setSchemaLoading(false);
        })
        .catch((err) => {
          console.warn('Failed to load GraphQL schema:', err);
          setSchemaLoading(false);
        });
    }
  }, [property, graphqlSchema, schemaLoading]);

  // Debug logging
  if (property.key === 'selectedTools') {
    // console.log('🔍 DynamicPropertyField - selectedTools property:', {
    //   type: property.type,
    //   customComponent: (property as any).customComponent,
    //   value,
    //   property
    // });
  }

  // Wrap entire field rendering in error boundary
  const renderField = () => {
    // Check for custom components first
    if ((property as any).customComponent === 'MCPTestMessageField') {
      const currentNode = nodes?.find(n => n.id === nodeId);
      return (
        <MCPTestMessageField
          value={value}
          onChange={onChange}
          nodeData={currentNode?.data || {}}
        />
      );
    }

    if ((property as any).customComponent === 'ChatTriggerTestInputField') {
      return (
        <ChatTriggerTestInputField
          value={value}
          onChange={onChange}
        />
      );
    }

    // Special handling for embedCode field
    if (property.key === 'embedCode') {
      return (
        <EmbedCodeField workflowId={currentWorkflowId || undefined} />
      );
    }

    if ((property as any).customComponent === 'WorkflowSelector') {
      return (
        <WorkflowSelectorField
          property={property}
          value={value}
          onChange={onChange}
          error={error}
        />
      );
    }

    if ((property as any).customComponent === 'FileUpload') {
      return (
        <FileUploadField
          property={property}
          value={value}
          onChange={onChange}
          onParameterChange={onParameterChange}
          error={error}
          allParameters={allParameters}
          inputData={inputData}
        />
      );
    }

    // Check for mode toggle enabled fields (text/data toggle)
    if ((property as any).modeToggle?.enabled) {
      const currentNode = nodes?.find(n => n.id === nodeId);
      const nodeType = currentNode?.data?.type || currentNode?.type;
      // Get credential ID from node's parameters (usually stored as 'credentialId' or 'credential')
      const credentialId = allParameters?.credentialId || allParameters?.credential || currentNode?.data?.credentialId;

      // Handler for mode changes - stores mode as {fieldKey}_mode
      const handleModeChange = (fieldKey: string, mode: 'text' | 'data') => {
        if (onParameterChange) {
          onParameterChange(`${fieldKey}_mode`, mode);
        }
      };

      return (
        <ModeToggleField
          property={property as any}
          value={value}
          onChange={onChange}
          onModeChange={handleModeChange}
          error={error}
          nodeType={nodeType}
          credentialId={credentialId}
          allParameters={allParameters}
        />
      );
    }

    // Render appropriate field based on property type
    const propertyType = property.type as string;
    switch (propertyType) {
      case 'string':
      case 'text':
      case 'url':
      case 'email': {
      // Special onBlur handler for URL field on http-request nodes
      // Extracts query parameters from URL and merges with queryParams field
      const currentNode = nodes?.find(n => n.id === nodeId);
      const isHttpRequestUrlField = property.key === 'url' &&
        (currentNode?.type === 'http-request' || currentNode?.data?.type === 'http-request');

      console.log('🔍 [DynamicPropertyField] URL field check:', {
        propertyKey: property.key,
        nodeId,
        currentNodeType: currentNode?.type,
        currentNodeDataType: currentNode?.data?.type,
        isHttpRequestUrlField,
        hasOnBatchParameterChange: !!onBatchParameterChange
      });

      const handleUrlBlur = isHttpRequestUrlField && onBatchParameterChange
        ? (urlValue: string) => {
            console.log('🔍 [DynamicPropertyField] URL blur triggered:', { urlValue });
            if (!urlValue) return;

            try {
              // Fix common mistake: & instead of ? for first query param
              let fixedUrl = urlValue;
              const questionMarkIndex = urlValue.indexOf('?');
              const ampersandIndex = urlValue.indexOf('&');

              // If there's an & but no ? before it, the first & should be ?
              if (ampersandIndex !== -1 && (questionMarkIndex === -1 || ampersandIndex < questionMarkIndex)) {
                fixedUrl = urlValue.substring(0, ampersandIndex) + '?' + urlValue.substring(ampersandIndex + 1);
              }

              // Parse the URL
              const url = new URL(fixedUrl);
              const searchParams = url.searchParams;

              // If there are query parameters in the URL
              if (searchParams.toString()) {
                // Get existing queryParameters (fixedCollection format)
                const existingParams = allParameters?.queryParameters?.parameters || [];

                // Extract params from URL and convert to fixedCollection format
                const extractedParams: { name: string; value: string }[] = [];
                searchParams.forEach((paramValue, key) => {
                  // Check if this param already exists
                  const existingIndex = existingParams.findIndex((p: any) => p.name === key);
                  if (existingIndex === -1) {
                    extractedParams.push({ name: key, value: paramValue });
                  }
                });

                // Merge: existing params + new params from URL
                const mergedParams = [...existingParams, ...extractedParams];

                // Update URL to base (without query params)
                const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;

                console.log('🔍 [DynamicPropertyField] Extracting query params:', {
                  originalUrl: urlValue,
                  baseUrl,
                  extractedParams,
                  mergedParams
                });

                // Use batch update to update all parameters atomically
                // This avoids race conditions from multiple sequential updates
                console.log('🔍 [DynamicPropertyField] Batch updating URL and query params');
                onBatchParameterChange({
                  url: baseUrl,
                  sendQuery: true,
                  queryParameters: { parameters: mergedParams }
                });
              }
            } catch (err) {
              // Invalid URL, don't parse - just leave it as is
              console.log('🔍 [DynamicPropertyField] URL parse error:', err);
            }
          }
        : undefined;

        return (
          <StringField
            property={property}
            value={value}
            onChange={onChange}
            onBlur={handleUrlBlur}
            error={error}
            allParameters={allParameters}
            inputData={inputData}
            onOpenAIHelp={onOpenAIHelp}
            nodeType={nodeType}
          />
        );
    }

      case 'number':
      case 'integer':
        return (
          <NumberField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
            inputData={inputData}
          />
        );

      case 'boolean':
        return (
          <BooleanField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
          />
        );

      case 'select':
      case 'enum':
        return (
          <SelectField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
            nodeType={nodeType}
            nodeProperties={allParameters}
            credentialId={credentialId}
            onBatchParameterChange={onBatchParameterChange}
          />
        );

      case 'array':
        return (
          <ArrayField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
          />
        );

      case 'object':
      case 'json':
        return (
          <ObjectField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
          />
        );

      case 'code': {
        // Get language from typeOptions
        const codeLanguage = ((property as any).typeOptions?.language || 'plaintext') as EditorLanguage;
        const rows = (property as any).typeOptions?.rows || 10;
        const height = `${rows * 22}px`; // Approximate line height

        return (
          <div className="space-y-2 min-w-0 w-full overflow-hidden">
            <div className="flex items-center gap-2">
              <Label htmlFor={property.key}>
                {property.label}
                {property.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              {property.helpText && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">{property.helpText}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {property.description && (
              <p className="text-sm text-muted-foreground">{property.description}</p>
            )}
            <CodeMirrorEditor
              value={value || ''}
              onChange={onChange}
              language={codeLanguage}
              height={height}
              placeholder={(property as any).placeholder}
              graphqlSchema={graphqlSchema || undefined}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        );
      }

      case 'credential':
        return (
          <CredentialField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
          />
        );

      case 'collection':
        return (
          <CollectionField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
          />
        );

      case 'fixedCollection':
        return (
          <FixedCollectionPropertyRenderer
            property={property as any}
            value={value || {}}
            onChange={onChange}
            error={error}
            nodeId={nodeId}
            nodes={nodes}
          />
        );

      case 'fieldMapping':
        return (
          <FieldMappingField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
            nodeId={nodeId}
          />
        );

      case 'notice':
        return (
          <NoticeField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
            nodeId={nodeId}
            nodes={nodes}
          />
        );

    case 'button':
      return (
        <ButtonField
          property={property}
          value={value}
          onChange={onChange}
          onPropertyChange={onPropertyChange}
          error={error}
          nodeId={nodeId}
          workflowId={currentWorkflowId || undefined}
        />
      );

      case 'chatVisibility':
        return (
          <ChatVisibilityField
            value={value || []}
            onChange={onChange}
          />
        );

      case 'options':
        // Options type should be handled as select
        return (
          <SelectField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
            nodeType={nodeType}
            nodeProperties={allParameters}
            credentialId={credentialId}
            onBatchParameterChange={onBatchParameterChange}
          />
        );

      case 'multiOptions':
        // Check if this is an MCP tool selector
        if ((property as any).customComponent === 'MCPToolSelector') {
          return (
            <MCPToolSelectorField
              property={property}
              value={value}
              onChange={onChange}
              error={error}
              nodeId={nodeId}
            />
          );
        }
        // Generic multiOptions (not implemented yet, fallback to string)
        return (
          <StringField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
            allParameters={allParameters}
            inputData={inputData}
            onOpenAIHelp={onOpenAIHelp}
            nodeType={nodeType}
          />
        );

      case 'jsonSchemaEditor':
        // Enhanced JSON Schema editor with validation, preview, templates, and testing
        const schemaEditorValue = typeof value === 'string' || typeof value === 'object' ? value : String(value ?? '');
        return (
          <div className="space-y-2 min-w-0 w-full overflow-hidden">
            <div className="flex items-center gap-2">
              <Label htmlFor={property.key}>
                {property.label}
                {property.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              {property.helpText && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">{property.helpText}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {property.description && (
              <p className="text-sm text-muted-foreground">{property.description}</p>
            )}
            <JsonSchemaEditorRenderer
              property={property as any}
              value={schemaEditorValue as string | object}
              onChange={onChange}
              disabled={property.readOnly}
              error={error}
              outputType={allParameters?.expectedOutputType || 'either'}
              onOutputTypeChange={onParameterChange ? (type) => onParameterChange('expectedOutputType', type) : undefined}
              schemaDescription={allParameters?.schemaDescription || ''}
              onSchemaDescriptionChange={onParameterChange ? (desc) => onParameterChange('schemaDescription', desc) : undefined}
            />
          </div>
        );

      case 'hidden':
        // Hidden properties are stored in parameters but not rendered
        return null;

      default:
        // Fallback to string field for unknown types
        return (
          <StringField
            property={property}
            value={value}
            onChange={onChange}
            error={error}
            allParameters={allParameters}
            inputData={inputData}
            onOpenAIHelp={onOpenAIHelp}
            nodeType={nodeType}
          />
        );
    }
  };

  return (
    <PropertyErrorBoundary propertyKey={property.key}>
      {renderField()}
    </PropertyErrorBoundary>
  );
}
